import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  Squares2X2Icon,
  UsersIcon,
  CreditCardIcon,
  InboxStackIcon,
  TicketIcon,
  HeartIcon,
  BellIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  MoonIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { RocketLaunchIcon } from '@heroicons/react/24/solid';

// Updated paths to match the route definitions
const adminNavItems = [
  { name: 'Dashboard', path: '/', icon: Squares2X2Icon },
  { name: 'User Management', path: '/users', icon: UsersIcon },
  { name: 'Subscriptions', path: '/subscriptions', icon: CreditCardIcon },
  { name: 'Sales & Enquiries', path: '/sales_and_enquiries', icon: InboxStackIcon },
  { name: 'Support Tickets', path: '/support_tickets', icon: TicketIcon },
  { name: 'System Health', path: '/system_health', icon: HeartIcon },
];

export default function AdminLayout() {
  const location = useLocation();

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
      {/* Admin Sidebar */}
      <aside className="w-[280px] bg-white border-r border-slate-200 flex flex-col justify-between hidden md:flex shrink-0">
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className="h-24 flex items-center px-8 shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 bg-brand-500 rounded-full flex items-center justify-center shadow-md shadow-brand-500/20 shrink-0">
                <RocketLaunchIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-[20px] font-extrabold text-slate-900 tracking-tight">Founder Finder</h1>
            </div>
          </div>
          
          {/* Navigation Links */}
          <nav className="px-5 space-y-1.5 flex-1 overflow-y-auto pt-4">
            {adminNavItems.map((item) => {
              const isActive = location.pathname === item.path; 
              return (
                <Link 
                  key={item.name} 
                  to={item.path} 
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-semibold transition-all duration-200 ${
                    isActive 
                      ? "bg-brand-50 text-brand-600 font-black" 
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <item.icon className={`w-[22px] h-[22px] shrink-0 ${isActive ? 'text-brand-600' : 'text-slate-400'}`} strokeWidth={isActive ? 2 : 1.5} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="p-6 flex flex-col gap-2 shrink-0">
          <div className="bg-emerald-50 rounded-xl p-4 mb-2">
            <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-1">System Status</p>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-[13px] font-bold text-emerald-700">System Status: Active</span>
            </div>
          </div>

          <button className="w-full flex items-center gap-3.5 px-3 py-2.5 text-[15px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-colors">
            <BellIcon className="w-6 h-6 text-slate-400 shrink-0" /> Notifications
          </button>
          
          {/* Changed to Link to route to /profile_settings */}
          <Link to="/profile_settings" className={`w-full flex items-center gap-3.5 px-3 py-2.5 text-[15px] font-semibold transition-colors rounded-xl ${
            location.pathname === '/profile_settings' 
              ? 'bg-brand-50 text-brand-600' 
              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
          }`}>
            <Cog6ToothIcon className={`w-6 h-6 shrink-0 ${location.pathname === '/profile_settings' ? 'text-brand-600' : 'text-slate-400'}`} strokeWidth={location.pathname === '/profile_settings' ? 2 : 1.5} /> Settings
          </Link>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Topbar */}
        <header className="h-[88px] bg-white flex items-center justify-between px-10 shrink-0 relative z-20">
          
          {/* Top Left: Header Tabs */}
          <div className="flex items-center gap-8">
            <h2 className="text-[20px] font-extrabold text-slate-900">Elite Admin V2</h2>
            <nav className="hidden lg:flex items-center gap-6 h-full">
              <button className="text-[14px] font-bold text-slate-500 hover:text-slate-900 transition-colors">Global Overview</button>
              <button className="text-[14px] font-bold text-brand-600 border-b-2 border-brand-500 pb-1">Audit Logs</button>
              <button className="text-[14px] font-bold text-slate-500 hover:text-slate-900 transition-colors">API Metrics</button>
            </nav>
          </div>
          
          {/* Right Header Actions */}
          <div className="flex items-center gap-6">
            
            {/* Search Bar */}
            <div className="hidden md:block w-72">
              <div className="relative group">
                <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-brand-500 transition-colors" />
                <input 
                  type="text" 
                  placeholder="Search parameters..." 
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-100/80 hover:bg-slate-100 border-none rounded-full text-[14px] font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:bg-white transition-all" 
                />
              </div>
            </div>

            <div className="flex items-center gap-3 text-slate-500">
              <button className="hover:text-slate-900 hover:bg-slate-50 p-2 rounded-full transition-colors">
                <QuestionMarkCircleIcon className="w-6 h-6" />
              </button>
              <button className="hover:text-slate-900 hover:bg-slate-50 p-2 rounded-full transition-colors">
                <MoonIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="h-8 w-px bg-slate-200" aria-hidden="true" />

            {/* Profile */}
            <div className="flex items-center gap-3 cursor-pointer group">
              <div className="text-right hidden sm:block">
                <div className="text-[13px] font-extrabold text-slate-900 leading-tight">Admin Console</div>
                <div className="text-[11px] text-slate-500 font-medium">Level 4 Access</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center border border-slate-300 overflow-hidden shadow-sm group-hover:shadow-md transition-shadow">
                <img src="https://i.pravatar.cc/150?img=11" alt="Admin" className="w-full h-full object-cover" />
              </div>
            </div>

          </div>
        </header>

        {/* Dynamic Page Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}